/**
 * hydrology_load — fetch the baked hydrology file for a seed and register its
 * river network with the terrain oracle (`setRiverNetwork`). Runs on the main
 * thread AND inside every terrain worker (chunk, far tier, water bake): the
 * carve must exist wherever `terrainHeight` is evaluated, or a chunk built
 * before the network arrived would miss its channels. Workers therefore gate
 * their job handling on the returned promise.
 *
 * Absent file (404, offline, seed without a bake) ⇒ no rivers, terrain as
 * before — never an error.
 */
import { setRiverNetwork, setRiverNetworkTable, riverNetworkTable } from '@shared/terrain.js';
import { hydrologyFileFor, hydrologyFileProblem, decodeHydrologyRivers, type HydroFile } from './hydrology_bake.js';
import { setBakedLakes } from './lakes.js';

const loaded = new Map<string, Promise<HydroFile | null>>();
const decoded = new Map<string, number[][]>();

/** The decoded rivers of a seed (flat RIVER_STRIDE arrays), once loaded. */
export function loadedRivers(seed: string): number[][] | null { return decoded.get(seed) ?? null; }

/** Fetch + register once per seed (idempotent; concurrent callers share). */
export function loadHydrology(seed: string, base = ''): Promise<HydroFile | null> {
  let p = loaded.get(seed);
  if (p) return p;
  p = (async (): Promise<HydroFile | null> => {
    try {
      const res = await fetch(`${base}/hydrology/${hydrologyFileFor(seed)}`);
      if (!res.ok) return null;
      const json: unknown = await res.json();
      const problem = hydrologyFileProblem(json, seed);
      if (problem) { console.warn(`[hydrology] ignoring ${hydrologyFileFor(seed)}: ${problem}`); return null; }
      const file = json as HydroFile;
      const rivers = decodeHydrologyRivers(file);
      setRiverNetwork(seed, rivers);
      const lakesAll = file.windows.flatMap((w) => w.lakes);
      setBakedLakes(seed, lakesAll);
      lakesBySeed.set(seed, lakesAll);
      decoded.set(seed, rivers);
      return file;
    } catch {
      return null;
    }
  })();
  loaded.set(seed, p);
  return p;
}

/** The file already loaded for a seed (null until/unless loadHydrology resolved it). */
export function loadedHydrology(seed: string): Promise<HydroFile | null> | null {
  return loaded.get(seed) ?? null;
}

/**
 * SHARED DATASET (perf frontier item 12): the main thread decodes and
 * indexes the network once and hands each terrain worker the typed-array
 * table (a structured-clone copy, ~14 MB, tens of ms) instead of every
 * worker fetching, parsing and indexing its own. Workers still fall back
 * to their own fetch when a job arrives before the table.
 */
export interface RiverTableMessage { kind: 'rivers'; seed: string; table: NonNullable<ReturnType<typeof riverNetworkTable>>; lakes: unknown[] }

/** Post the loaded table to a worker (no-op until the file has loaded). */
export function postRiverTable(worker: { postMessage(m: unknown): void }, seed: string): boolean {
  const table = riverNetworkTable(seed);
  if (!table) return false;
  const lakes = bakedLakesOf(seed);
  const msg: RiverTableMessage = { kind: 'rivers', seed, table, lakes };
  worker.postMessage(msg);
  return true;
}

/** Worker side: adopt a posted table and mark the seed loaded (jobs stop waiting on a fetch). */
export function adoptRiverTable(msg: RiverTableMessage): void {
  setRiverNetworkTable(msg.seed, msg.table);
  setBakedLakes(msg.seed, msg.lakes as Parameters<typeof setBakedLakes>[1]);
  loaded.set(msg.seed, Promise.resolve(null));
}

const lakesBySeed = new Map<string, unknown[]>();
function bakedLakesOf(seed: string): unknown[] { return lakesBySeed.get(seed) ?? []; }
